import React, { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import Loader from 'src/components/Loader';

import { useTranslation } from 'react-i18next';
import { getPreSignedUrl } from 'src/utils/helpers';
import ButtonTag from 'src/components/ButtonTag';
import Checkbox from 'src/components/Checkbox';
import Styled from 'src/components/JobMobileDataTab/JobMobilData.styles';

const _ = require('lodash');

const ImageGrid = (
  { allRecords,
    isEditableByUser,
    isJobUnderReview,
    includeInPhotoForm,
    openImagePreview,
    records,
    reorder,
    setIsInFindings,
  }) => {

  const [chunkedRecords, setChunkedRecords] = useState([]);

  const { t } = useTranslation();

  const isDragDisabled = useMemo(() => !isEditableByUser || !isJobUnderReview,
    [isEditableByUser, isJobUnderReview]);

  if (records.length === 0 && records.constructor === Object) {
    return <Loader />;
  }

  useEffect(() => {
    Promise.allSettled(records.map(async record => {
      const jobAttachmentId = (record.showAltered ? record.alteredAttachmentId : record.attachmentId) || null;
      if (jobAttachmentId) {
        // eslint-disable-next-line no-param-reassign
        record.src = await getPreSignedUrl(jobAttachmentId, null, 'md');
      }
      return record;
    }))
      .then(result => {
        const chunkifiedRecordsArray = _.chunk(_.compact(result.map(o => o.value)), 3);
        setChunkedRecords(chunkifiedRecordsArray);
      });
  }, [records]);

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    if (typeof reorder === 'function') {
      await reorder(result.source.index, result.destination.index);
    }
  };

  const getItemStyle = (isDragging, draggableStyle) => ({
    background: isDragging ? 'var(--ph-light-blue-100)' : 'var(--white)',
    ...draggableStyle,
  });

  function getPosition(id) {
    return records.findIndex(value => value.id === id);
  }

  const getImageCaption = (recordId) => {
    return ((records || []).find(record => record.id === recordId)?.tags || [])
      .filter(tag => !!tag.name)
      .map(tag => <ButtonTag key={tag.id} showCrossIcon={false} text={tag.name} />);
  };

  const showQuestionIndicator = (photoRecord) => {
    if (!photoRecord) {
      return false;
    }
    return !!allRecords.find(record => record.filename === photoRecord.filename &&
      !record.isDeleted && record.questionId && record.checklistId);
  };

  return (
    <Styled.Container readOnly={!isEditableByUser}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Styled.DragDropContainer>
          {chunkedRecords.map((chunkRecord, index) => (
            <Droppable key={index}
                       direction="horizontal"
                       droppableId={`mobileDataImageGridDroppable-${index}`}
                       ignoreContainerClipping={true}>
              {(droppableProvided) => (
                <Styled.Grid
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}>
                  {droppableProvided.innerRef}
                  {
                    chunkRecord.map((item) => (
                      <Draggable
                        key={`${item.id}`}
                        draggableId={`${item.id}`}
                        index={getPosition(item.id)}
                        isDragDisabled={isDragDisabled}>
                        {(draggableProvided, draggableSnapshot) => (
                          <Styled.ImageHolder
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            key={item.id}
                            style={getItemStyle(
                              draggableSnapshot.isDragging,
                              draggableProvided.draggableProps.style,
                            )}>
                            <Styled.Figure>
                              <Styled.Image onClick={() => openImagePreview(item.id)} src={item.src} />
                              {showQuestionIndicator(item) && <Styled.LinkedQuestionsIndicator>
                                <Styled.LinkedQuestionsIndicatorIcon name="dash" />
                                <Styled.LinkedQuestionsIndicatorIcon name="check" />
                                <Styled.LinkedQuestionsIndicatorIcon name="dash" />
                                <Styled.LinkedQuestionsIndicatorIcon name="check" />
                              </Styled.LinkedQuestionsIndicator>
                              }
                              <Styled.FigureCaption>{getImageCaption(item.id)}</Styled.FigureCaption>
                            </Styled.Figure>
                            <Styled.ImageActions>
                              <Checkbox
                                label={t('INCLUDE_PHOTO_IN_FORM_CHECKBOX_LABEL')}
                                onChange={(ev) => includeInPhotoForm(
                                  item.showFindings,
                                  item.id,
                                  item.order,
                                  ev.target.checked,
                                  {
                                    showAltered: item.showAltered,
                                    alteredFileName: item.alteredFileName,
                                    alteredAttachmentId: item.alteredAttachmentId
                                  }
                                )}
                                reverse={true}
                                value={item.viewInPhotoForm} />
                              <Checkbox
                                label={t('INCLUDE_PHOTO_IN_FINDINGS_CHECKBOX_LABEL')}
                                onChange={(ev) => setIsInFindings(
                                  item.viewInPhotoForm,
                                  item.id,
                                  item.order,
                                  ev.target.checked,
                                  {
                                    showAltered: item.showAltered,
                                    alteredFileName: item.alteredFileName,
                                    alteredAttachmentId: item.alteredAttachmentId
                                  }
                                )}
                                reverse={true}
                                value={item.showFindings} />
                            </Styled.ImageActions>

                          </Styled.ImageHolder>
                        )}
                      </Draggable>
                    ))}
                  {droppableProvided.placeholder}
                </Styled.Grid>
              )}
            </Droppable>))}
        </Styled.DragDropContainer>
      </DragDropContext>
    </Styled.Container>);
};

ImageGrid.propTypes = {
  allRecords: PropTypes.array.isRequired,
  includeInPhotoForm: PropTypes.func.isRequired,
  isEditableByUser: PropTypes.bool.isRequired,
  isJobUnderReview: PropTypes.bool.isRequired,
  openImagePreview: PropTypes.func.isRequired,
  records: PropTypes.array.isRequired,
  reorder: PropTypes.func.isRequired,
  setIsInFindings: PropTypes.func.isRequired,
};

export default React.memo(ImageGrid);
